/**
 * Method mixins
 * @module methods
 */

import config from './config'
import events from './events'

const methods = {
  methods: {
    /**
     * Store a page DTO in the $root for the session
     * @method addPageToCache
     * @param {string} slug [slug='']
     * @param {number|string} id [id='']
     * @param {null|object} data [data=null]
     * @return {void}
     */
    addPageToCache (slug = '', id = '', data = null) {
      const cache = this.$root.cache
      if (cache[slug]) {
        cache[slug][id] = data
      }
    },
    /**
     * Get the page DTO from the session cache
     * @method getCachedPage
     * @param {string} slug [slug='']
     * @param {number|string} id [id='']
     * @param {string} key [key='']
     * @return {object}
     */
    getCachedPage (slug = '', id = '', key = '') {
      let cache = this.$root.cache[slug][id]
      if (cache) {
        window.scrollTo(0, 0)
        return key ? this.$root.cache[slug][id][key] : cache
      }
      return null
    },
    /**
     * Simple date formatting
     * @method parseExternalHtml
     * @param {string} date [date='']
     * @param {string} delimiter [delimiter='/']
     * @param {string} splitter [splitter='/']
     * @return {string}
     */
    simpleDateFormat (date = '', delimiter = '/', splitter = '-') {
      const dateFormatted = []
      date.split(splitter).forEach(item => {
        if (item !== '00') {
          dateFormatted.push(item)
        }
      })
      return dateFormatted.join(delimiter)
    },
    /**
     * Parse externally loaded html and format it properly
     * @method parseExternalHtml
     * @param {string} html [html='']
     * @return {string}
     */
    parseExternalHtml (html = '') {
      html = html
        .replace(/(\r\n|\n|\r)/gm, '')
        .replace(/>\s</gi, '><')
        .replace(/<b>/gi, '<h3 class="title font-weight-light mt-3">')
        .replace(/<\/b>/gi, '</h3>')
        .replace(/<br><br>/gi, '<br />')
        .replace(/<a/gi, `<a class="external-link"`)
        .trim()
      return html
    },
    /**
     * Set breadcrumbs color according to the theme selected
     * @method setBreadcrumbsColor
     * @return {void}
     */
    setBreadcrumbsColor () {
      this.$nextTick(() => {
        document.querySelectorAll('.v-breadcrumbs__item').forEach(item => {
          item.classList.remove('v-breadcrumbs__item--disabled')
          item.classList.add(this.$root.config.dark ? 'white--text' : 'black--text')
        })
      })
    },
    /**
     * Emit route change event
     * @method emitRouteChange
     * @return {void}
     */
    emitRouteChange () {
      this.$root.$emit(events.route)
    },
    /**
     * Get grid data by resource, id and verb and store the session cache
     * @method getTabularData
     * @param {string|null} resource [resource=null]
     * @param {string|null} id [id=null]
     * @param {string|null} verb [id=null]
     * @param {function||null} callback [callback=null]
     * @return {void}
     */
    getTabularData (resource = null, id = null, verb = null, callback = null) {
      const cache = this.getCachedPage('grids', this.$route.path)
      const url = this.$root.routes.find(route => route.url.includes(resource + '/:' + verb)).url.replace(':' + verb, id)
      if (cache) {
        this.headers = cache.headers
        this.items = cache.items
        this.$nextTick(() => {
          this.emitRouteChange()
          if (callback && typeof callback === 'function') {
            callback()
          }
        })
        return
      }
      this.$http(url)
        .then(response => {
          const data = response.data.payload
          const headers = []
          if (data.bands.length) {
            Object.keys(data.bands[0]).forEach((header, index) => {
              let cssClass
              switch (index) {
                case 0:
                case 4:
                  cssClass = 'hidden-sm-and-down'
                  break
                case 2:
                  cssClass = 'hidden-xs-only'
                  break
                default:
                  cssClass = null
              }
              headers.push({
                text: this.lang[header],
                align: 'left',
                sortable: true,
                value: header,
                class: cssClass
              })
            })
          }
          this.headers = headers
          this.items = data.bands
          this.addPageToCache('grids', this.$route.path, {headers: this.headers, items: this.items})
          this.$nextTick(() => {
            this.emitRouteChange()
            if (callback && typeof callback === 'function') {
              callback()
            }
          })
        })
    },
    /**
     * Set the last breadcrums item text
     * @method setLastBreadcrumbsItemText
     * @param {string|null} text [text=""]
     * @return {void}
     */
    setLastBreadcrumbsItemText (text = '') {
      text = decodeURIComponent(text)
      this.$nextTick(() => {
        const bcElements = document.querySelectorAll('.v-breadcrumbs li a')
        const titleElements = [this.$root.config.siteTitle]
        const item = document.querySelector('.v-breadcrumbs li:last-child a')
        if (item) {
          item.innerHTML = text
          item.setAttribute('title', text)
          bcElements.forEach(item => {
            titleElements.push(item.innerText)
          })
          titleElements.splice(1, 1)
          document.title = titleElements.join(' | ')
          this.setMetaTags(titleElements)
        }
      })
    },
    /**
     * Set meta tags
     * @method setMetaTags
     * @param {array} keywords [keywords=[]]
     * @return {void}
     */
    setMetaTags (keywords = []) {
      const keywordsMeta = document.querySelector('meta[name="keywords"]')
      const descriptionMeta = document.querySelector('meta[name="description"]')
      const descriptionSource = document.querySelector('.meta-description')
      if (keywordsMeta) {
        keywordsMeta.setAttribute('content', keywords.reverse().join(', ').toLowerCase())
      }
      if (descriptionMeta) {
        if (descriptionSource) {
          let content = descriptionSource.innerText.replace(/\r\n|\n|\r/g, '')
          content = this.truncate(content)
          descriptionMeta.setAttribute('content', content)
        }
      }
    },
    /**
     * Truncate text by max length, optional side and ellipsis characters
     * @method truncate
     * @param {string} content [content='']
     * @param {number} maxLength [maxLength=256]
     * @param {object} options [{side='end', ellipsis='...'}]
     * @return {string}
     */
    truncate (content = '', maxLength = 158, {side = 'end', ellipsis = '...'} = {}) {
      if (content.length > maxLength) {
        switch (side) {
          case 'start':
            return ellipsis + content.slice(-(maxLength - ellipsis.length))
          case 'end':
          default:
            return content.slice(0, maxLength - ellipsis.length) + ellipsis
        }
      }
      return content
    },
    /**
     * Push new path to the router
     * @method setLastBreadcrumbsItemText
     * @param {string} resource [resource='']
     * @param {string} verb [verb='']
     * @return {void}
     */
    goToResource (resource = '', verb = '') {
      const path = `${resource}/${verb}`
      if (path !== this.$route.path) {
        this.$router.push({path})
      }
    },
    /**
     * Set document.title
     * @method setDocumentTitle
     * @param {string} pageTitle [pageTitle='']
     * @param {string} delimiter [delimiter=' | ']
     * @return {void}
     */
    setDocumentTitle (pageTitle = '', delimiter = ' | ') {
      if (pageTitle) {
        document.title = `${config.siteTitle}${pageTitle ? delimiter : ''}${decodeURIComponent(pageTitle)}`
      }
    }
  }
}

export default methods



// WEBPACK FOOTER //
// ./src/helpers/methods.js